import { useEffect, useState } from 'react';
import type { RxDocument } from 'rxdb';
import { useRxCollection } from 'rxdb-hooks';
import { v4 as uuidv4 } from 'uuid';

import { sentryCaptureException } from '../../utilities';
import { FALSE, TRUE } from '../../utilities/constants';
import { getValueFromLocalStorage, isNull } from '../../utilities/utilityFunctions';
import { RxdbCollectionName } from '../rxdbCollectionName';
import { DeficiencyVersionFiles, DeficiencyVersionFilesCollection } from './queryBuilder';

export const useDeficiencyVersionFiles = () => {
  const deficiencyVersionFilesCollection: DeficiencyVersionFilesCollection = useRxCollection(
    RxdbCollectionName.DEFICIENCY_VERSION_FILES,
  );
  const [deficiencyVersionFilesInitialized, setDeficiencyVersionFilesInitialized] =
    useState<boolean>(false);

  useEffect(() => {
    if (deficiencyVersionFilesCollection) {
      setDeficiencyVersionFilesInitialized(true);
    }
  }, [deficiencyVersionFilesCollection]);

  const insertFileIntoActiveDeficiencyFile = async (fileObj: DeficiencyVersionFiles) => {
    try {
      if (fileObj.deficiencyUUID !== '') await deficiencyVersionFilesCollection?.upsert(fileObj);
    } catch (error) {
      sentryCaptureException(
        error,
        '~ file: useDeficiencyFiles.tsx ~ line 38 ~ insertFileIntoDeficiencyFile ~',
      );
      throw error;
    }
  };

  /**
   * Subscribe to newly inserted attachments
   *
   * @param {(item: RxChangeEventInsert<DeficiencyVersionFiles>) => void} next
   * @return {*}
   */
  const subscribeToDefAttachments = (
    handleAttachments: (attachments: DeficiencyVersionFiles[]) => void,
  ) => {
    if (!deficiencyVersionFilesCollection) return;
    const query = deficiencyVersionFilesCollection.find();
    query.$.subscribe(handleAttachments);
  };

  const updateActiveDeficiencyFile = async (
    fileObj: DeficiencyVersionFiles,
  ): Promise<RxDocument<DeficiencyVersionFiles> | null> => {
    if (!deficiencyVersionFilesCollection) return null;
    const filePayload: DeficiencyVersionFiles = {
      fileName: fileObj?.fileName,
      showOnReport: fileObj.showOnReport || false,
      fileDescription: fileObj.fileDescription || '',
      isDelete: FALSE,
      fileSortOrder: fileObj.fileSortOrder || 0,
      fkAimsfileTblSp: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
      userModified: '',
      isReInspectedFile: fileObj.isReInspectedFile || true,
      componentVersionInspectionId: fileObj.componentVersionInspectionId,
      componentVersionId: fileObj.componentVersionId,
      deficiencyUUID: fileObj.deficiencyUUID,
      originalFileName: fileObj.originalFileName,
      path: fileObj.path,
      isImageFile: fileObj.isImageFile,
      deficiencyId: fileObj.deficiencyId,
      inspectionId: fileObj.inspectionId,
      drawingType: fileObj.drawingType,
      assetId: fileObj.assetId,
      fileCategory: fileObj.fileCategory,
      pcsId: fileObj.pcsId,
      siteId: fileObj.siteId,
      disciplineId: fileObj.disciplineId,
      assetInspectionTask: fileObj.assetInspectionTask,
      fkInspectorField: Number(getValueFromLocalStorage('taskFieldInspectorId')),
      fkInspectorLead: Number(getValueFromLocalStorage('taskLeadInspectorId')),
      fileComponentVersionUUId: isNull(fileObj, 'fileComponentVersionUUId', uuidv4()),
      fileAimsUUID: fileObj.fileAimsUUID,
      fileId: fileObj.fileId,
      extension: fileObj.extension,
      sizeMb: fileObj.sizeMb,
      allowSpaccess: fileObj.allowSpaccess,
      legacyId: 0,
      userCreated: fileObj.userCreated,
    };
    try {
      if (deficiencyVersionFilesCollection) {
        let updated = await deficiencyVersionFilesCollection
          .findOne({
            selector: {
              fileComponentVersionUUId: filePayload?.fileComponentVersionUUId,
            },
          })
          .update({
            $set: {
              fileName: fileObj?.fileName,
              showOnReport: fileObj.showOnReport,
              fileDescription: fileObj.fileDescription,
              isDelete: fileObj.isDelete,
              fileSortOrder: fileObj.fileSortOrder,
              fkAimsfileTblSp: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
              userModified: '',
              isReInspectedFile: fileObj.isReInspectedFile || false,
              componentVersionInspectionId: fileObj.componentVersionInspectionId,
              componentVersionId: fileObj.componentVersionId,
              fkInspectorField: filePayload.fkInspectorField,
              fkInspectorLead: filePayload.fkInspectorLead,
              legacyId: filePayload.legacyId,
            },
          });
        if (updated === null) {
          updated = await deficiencyVersionFilesCollection?.insert(filePayload);
        }
        return updated;
      }
    } catch (error) {
      sentryCaptureException(
        error,
        '~ file: useDeficiencyFile.tsx ~ line 118 ~ updateDeficiencyFile ~',
      );
      throw error;
    }
    return null;
  };
  const deleteActiveDeficiencyFile = async (
    fileObj: DeficiencyVersionFiles,
  ): Promise<RxDocument<DeficiencyVersionFiles> | null> => {
    if (!deficiencyVersionFilesCollection) return null;
    const filePayload: DeficiencyVersionFiles = {
      fileName: fileObj?.fileName,
      showOnReport: fileObj.showOnReport || false,
      fileDescription: fileObj.fileDescription || '',
      isDelete: TRUE,
      fileSortOrder: fileObj.fileSortOrder || 0,
      fkAimsfileTblSp: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
      userModified: '',
      isReInspectedFile: fileObj.isReInspectedFile || false,
      componentVersionInspectionId: fileObj.componentVersionInspectionId,
      componentVersionId: fileObj.componentVersionId,
      deficiencyUUID: fileObj.deficiencyUUID,
      originalFileName: fileObj.originalFileName,
      path: fileObj.path,
      isImageFile: fileObj.isImageFile,
      deficiencyId: fileObj.deficiencyId,
      inspectionId: fileObj.inspectionId,
      drawingType: fileObj.drawingType,
      assetId: fileObj.assetId,
      fileCategory: fileObj.fileCategory,
      pcsId: fileObj.pcsId,
      siteId: fileObj.siteId,
      disciplineId: fileObj.disciplineId,
      assetInspectionTask: fileObj.assetInspectionTask,
      fkInspectorField: Number(getValueFromLocalStorage('taskFieldInspectorId')),
      fkInspectorLead: Number(getValueFromLocalStorage('taskLeadInspectorId')),
      fileComponentVersionUUId: isNull(fileObj, 'fileComponentVersionUUId', uuidv4()),
      fileAimsUUID: fileObj?.fileAimsUUID,
      fileId: fileObj.fileId,
      extension: fileObj.extension,
      sizeMb: fileObj.sizeMb,
      allowSpaccess: fileObj.allowSpaccess,
      legacyId: 0,
      userCreated: fileObj.userCreated,
    };
    try {
      if (deficiencyVersionFilesCollection) {
        let updated = await deficiencyVersionFilesCollection
          .findOne({
            selector: {
              fileComponentVersionUUId: filePayload.fileComponentVersionUUId,
            },
          })
          .update({
            $set: {
              isDelete: TRUE,
              fkAimsfileTblSp: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
              userModified: '',
              isReInspectedFile: fileObj.isReInspectedFile || false,
              componentVersionInspectionId: fileObj.componentVersionInspectionId,
              componentVersionId: fileObj.componentVersionId,
              fkInspectorField: filePayload.fkInspectorField,
              fkInspectorLead: filePayload.fkInspectorLead,
              legacyId: filePayload.legacyId,
            },
          });
        if (updated === null) {
          updated = await deficiencyVersionFilesCollection?.insert({
            ...filePayload,
            isDelete: TRUE,
          });
        }
        return updated;
      }
    } catch (error) {
      sentryCaptureException(
        error,
        '~ file: useDeficiencyFile.tsx ~ line 155 ~ deleteDeficiencyFile ~',
      );
      throw error;
    }
    return null;
  };

  return {
    deficiencyVersionFilesCollection,
    deficiencyVersionFilesInitialized,
    subscribeToDefAttachments,
    insertFileIntoActiveDeficiencyFile,
    updateActiveDeficiencyFile,
    deleteActiveDeficiencyFile,
  };
};

export default useDeficiencyVersionFiles;
